'use client';

import React, { useEffect, useRef, useState } from 'react';
import Link from 'next/link';

interface Location {
  id: number;
  type: 'buffet' | 'market';
  name: string;
  city: string;
  description: string;
  address: string;
  phone: string;
  hours: string;
  map_embed_url: string;
  image: string;
}

const LocationsSection: React.FC = () => {
  const sectionRef = useRef<HTMLElement>(null);
  const [locations, setLocations] = useState<Location[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchLocations = async () => {
      try {
        const response = await fetch('/api/locations');
        if (response.ok) {
          const data = await response.json();
          setLocations(data);
        }
      } catch (error) {
        console.error('Failed to fetch locations:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchLocations();
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const reveals = entry.target.querySelectorAll('.reveal-on-scroll');
            reveals.forEach((el, i) => {
              setTimeout(() => el.classList.add('revealed'), i * 100);
            });
          }
        });
      },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, [locations]);

  const buffetLocations = locations.filter(l => l.type === 'buffet');
  const marketLocations = locations.filter(l => l.type === 'market');

  if (loading) {
    return (
      <section id="locations" className="py-32 bg-charcoal">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <p className="font-sans text-parchment/50">Chargement des points de vente...</p>
        </div>
      </section>
    );
  }

  return (
    <section id="locations" ref={sectionRef} className="py-32 bg-charcoal relative overflow-hidden">
      {/* Background glow */}
      <div className="absolute top-1/3 left-1/4 w-[400px] h-[400px] rounded-full bg-corsasia/5 blur-[120px] pointer-events-none" />
      <div className="absolute bottom-1/4 right-1/4 w-[300px] h-[300px] rounded-full bg-clay/5 blur-[100px] pointer-events-none" />

      <div className="max-w-7xl mx-auto px-6">
        {/* Header */}
        <div className="reveal-on-scroll text-center mb-20">
          <p className="text-clay text-xs uppercase tracking-widest font-sans font-medium mb-4">
            Nos Points de Vente
          </p>
          <h2
            className="font-serif italic text-parchment leading-tight mb-6"
            style={{ fontSize: 'clamp(2.5rem, 5vw, 4.5rem)' }}
          >
            6 adresses en Corse.
          </h2>
          <p className="font-sans text-parchment/50 text-lg max-w-2xl mx-auto">
            Trois Buffet d'Asie pour un voyage culinaire, et trois Market Traiteur pour emporter les saveurs chez vous.
          </p>
        </div>

        {/* Buffet d'Asie Section */}
        <div className="reveal-on-scroll mb-20">
          <div className="flex items-center gap-4 mb-10">
            <span className="bg-corsasia text-parchment px-4 py-1.5 rounded-full text-xs uppercase tracking-widest font-sans font-medium">
              Buffet d'Asie
            </span>
            <p className="font-sans text-parchment/40 text-sm">Restaurant • Buffet à volonté</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {buffetLocations.map((location) => (
              <LocationCard key={location.id} location={location} />
            ))}
          </div>
        </div>

        {/* Market Traiteur Section */}
        <div className="reveal-on-scroll">
          <div className="flex items-center gap-4 mb-10">
            <span className="bg-market text-parchment px-4 py-1.5 rounded-full text-xs uppercase tracking-widest font-sans font-medium">
              Market Traiteur
            </span>
            <p className="font-sans text-parchment/40 text-sm">Épicerie • Plats à emporter • Produits corses</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {marketLocations.map((location) => (
              <LocationCard key={location.id} location={location} />
            ))}
          </div>
        </div>

        {/* Info banner */}
        <div className="reveal-on-scroll mt-20 bg-charcoal-light/50 rounded-4xl p-8 border border-clay/10">
          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center">
              <p className="font-serif italic text-clay text-3xl mb-2">3</p>
              <p className="font-sans text-parchment/50 text-sm uppercase tracking-widest">Buffets d'Asie</p>
            </div>
            <div className="text-center border-y md:border-y-0 md:border-x border-clay/10 py-6 md:py-0">
              <p className="font-serif italic text-clay text-3xl mb-2">3</p>
              <p className="font-sans text-parchment/50 text-sm uppercase tracking-widest">Market Traiteur</p>
            </div>
            <div className="text-center">
              <p className="font-serif italic text-clay text-3xl mb-2">3</p>
              <p className="font-sans text-parchment/50 text-sm uppercase tracking-widest">Villes</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

interface LocationCardProps {
  location: Location;
}

const LocationCard: React.FC<LocationCardProps> = ({ location }) => {
  return (
    <Link
      href={`/locations/${location.id}`}
      className="group bg-charcoal-light/30 rounded-3xl overflow-hidden border border-clay/10 hover:border-clay/30 transition-all duration-500 hover:-translate-y-2 hover:shadow-2xl hover:shadow-charcoal/50 block"
    >
      {/* Image */}
      <div className="aspect-[16/10] overflow-hidden relative">
        <img
          src={location.image}
          alt={`${location.name} ${location.city}`}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-charcoal via-charcoal/20 to-transparent" />
        <div className="absolute bottom-4 left-4 right-4">
          <p className="font-serif italic text-parchment text-2xl">{location.city}</p>
        </div>
      </div>

      {/* Content */}
      <div className="p-6">
        <h3 className="font-sans font-semibold text-parchment text-lg mb-2">
          {location.name} {location.city}
        </h3>
        <p className="font-sans text-parchment/50 text-sm leading-relaxed mb-4 line-clamp-2">
          {location.description}
        </p>

        {/* Details */}
        <div className="space-y-3 mb-5">
          <div className="flex items-start gap-3">
            <svg className="w-4 h-4 text-clay mt-0.5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5">
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 10.5a3 3 0 11-6 0 3 3 0 016 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1115 0z" />
            </svg>
            <p className="font-sans text-parchment/60 text-sm">{location.address}</p>
          </div>
          <div className="flex items-center gap-3">
            <svg className="w-4 h-4 text-clay shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5">
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <p className="font-sans text-parchment/60 text-sm">{location.hours}</p>
          </div>
        </div>

        {/* View details */}
        <div className="inline-flex items-center gap-2 text-clay text-sm font-sans font-medium group-hover:gap-3 transition-all">
          <span>Voir les détails</span>
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5">
            <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3" />
          </svg>
        </div>
      </div>
    </Link>
  );
};

export default LocationsSection;